import React, { useEffect } from "react";
import ReactDOM from 'react-dom';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/authUtils';
import { FaHome, FaUserShield, FaUsers, FaServicestack, FaProjectDiagram, FaCalendarAlt, FaNewspaper, FaEnvelope, FaBook, FaChevronLeft, FaChevronRight, FaSignOutAlt } from 'react-icons/fa';

// Sidebar navigation items
import { FaHandshake, FaInfoCircle } from 'react-icons/fa';
const navItems = [
  { name: 'Dashboard Home', path: '/admin', icon: <FaHome className="text-green-700 text-xl md:text-2xl" /> },
  { name: 'Admin Users', path: '/admin/admin-management', icon: <FaUserShield className="text-green-700 text-xl md:text-2xl" /> },
  { name: 'Our Story', path: '/admin/about', icon: <FaInfoCircle className="text-green-700 text-xl md:text-2xl" /> },
  { name: 'Founders', path: '/admin/members', icon: <FaUsers className="text-green-700 text-xl md:text-2xl" /> },
  { name: 'Partners', path: '/admin/partners', icon: <FaHandshake className="text-green-700 text-xl md:text-2xl" /> },
  { name: 'Advisors', path: '/admin/advisors', icon: <FaUserShield className="text-green-700 text-xl md:text-2xl" /> },
  { name: 'Authors', path: '/admin/authors', icon: <FaBook className="text-green-700 text-base md:text-lg" /> },
  { name: 'Services', path: '/admin/services', icon: <FaServicestack className="text-green-700" /> },
  { name: 'Projects', path: '/admin/projects', icon: <FaProjectDiagram className="text-green-700" /> },
  { name: 'Events', path: '/admin/events', icon: <FaCalendarAlt className="text-green-700" /> },
  { name: 'Publications', path: '/admin/articles', icon: <FaNewspaper className="text-green-700" /> },
  { name: 'Contact Messages', path: '/admin/contact-messages', icon: <FaEnvelope className="text-green-700" /> },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const isAdmin = authAPI.hasRole('admin') || authAPI.hasRole('superadmin');

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  // Use global unread count from contact messages
  const [unreadCount, setUnreadCount] = React.useState(window.contactMessagesUnreadCount || 0);

  React.useEffect(() => {
    // Set initial unread count from global variable
    const initialCount = window.contactMessagesUnreadCount || 0;
    setUnreadCount(initialCount);
    console.log('✓ AdminDashboard initial unreadCount:', initialCount);
    
    const interval = setInterval(() => {
      const currentCount = window.contactMessagesUnreadCount || 0;
      setUnreadCount(currentCount);
      if (currentCount > 0) {
        console.log('Unread count updated:', currentCount);
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // State for mobile sidebar drawer
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  
  // State for desktop sidebar collapse - persisted in localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Update localStorage whenever sidebarCollapsed changes
  React.useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  return (
    <>
      {isAdmin && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          zIndex: 9999,
          margin: '0',
          padding: '0',
          border: 'none',
          background: 'transparent',
        }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className="bg-gradient-to-br from-blue-600 to-emerald-400 text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300 border-4 border-white/70 backdrop-blur-lg"
              style={{ 
                width: '4rem',
                height: '4rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => window.location.href = '/admin/contact-messages'}
              aria-label="Contact Messages"
            >
              <FaEnvelope className="text-3xl animate-bounce" />
            </button>
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white shadow-lg transform translate-x-1 -translate-y-1">
                {unreadCount}
              </span>
            )}
          </div>
        </div>,
        document.body
      )}
      <div
        className="w-full max-w-none rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl p-1 sm:p-2 md:p-4 lg:p-8 text-sm sm:text-base overflow-x-auto"
        style={{
          background: 'rgba(255, 255, 255, 0.65)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <div className="relative min-h-screen flex flex-col md:flex-row overflow-hidden p-1 sm:p-2 md:p-4 w-full md:max-w-screen-2xl md:mx-auto min-w-0">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 z-0">
          <style>{`
            @keyframes gradientMove {
              0% { background-position: 0% 0%; }
              100% { background-position: 100% 100%; }
            }
            ${[...Array(12)].map((_, i) => `@keyframes floatParticle${i} {
              0% { transform: translateY(0) scale(1); opacity: 0.4; }
              25% { transform: translateY(-40px) scale(1.3); opacity: 0.6; }
              50% { transform: translateY(-80px) scale(0.7); opacity: 0.3; }
              75% { transform: translateY(-120px) scale(1.1); opacity: 0.5; }
              100% { transform: translateY(-160px) scale(0); opacity: 0; }
            }`).join('\n')}
            .golden-frame {
              position: relative;
              border: 1px solid #C9A635;
              transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
            }
            .golden-frame:hover {
              border-color: #E6C55A;
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(201, 166, 53, 0.2);
            }
            .golden-frame.active {
              background-color: rgb(191, 219, 254) !important;
            }
            .logout-button {
              border: 2px solid #DC2626 !important;
            }
            .sidebar-tooltip {
              position: absolute;
              left: 100%;
              top: 50%;
              transform: translateY(-50%);
              margin-left: 0.5rem;
              padding: 0.5rem 0.75rem;
              background: rgba(0, 0, 0, 0.9);
              color: white;
              border-radius: 0.5rem;
              white-space: nowrap;
              opacity: 0;
              pointer-events: none;
              transition: opacity 0.2s;
              z-index: 1000;
              font-size: 0.875rem;
            }
            .sidebar-item:hover .sidebar-tooltip {
              opacity: 1;
            }
          `}</style>
        </div>

        {/* Sidebar for desktop */}
        <aside 
          className={`hidden md:flex flex-col bg-white/90 backdrop-blur-lg shadow-2xl p-4 rounded-3xl animate-slidein-left transition-all duration-300 border-2 border-blue-200 fixed z-30 left-0 overflow-y-auto overflow-x-hidden ${
            sidebarCollapsed ? 'w-22 min-w-22' : 'w-60 min-w-60'
          }`}
          style={{ height: '105vh', top: '0', marginTop: '3rem' }}
        >
          {/* Collapse/Expand Button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute top-4 -right-3 bg-blue-600 text-white rounded-full p-2 shadow-lg hover:bg-blue-700 transition-all duration-300 z-50"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <FaChevronRight size={16} /> : <FaChevronLeft size={16} />}
          </button>

          <div className={`mb-4 ${sidebarCollapsed ? 'hidden' : ''}`}>
            <h2 className="text-xl font-bold text-center text-blue-700">Admin Panel</h2>
          </div>
          
          <nav className="flex-1 overflow-y-auto overflow-x-hidden pr-2">
            <ul className="space-y-3 overflow-x-hidden">
              {navItems.map((item, idx) => (
                <li key={item.name} className="animate-fadein sidebar-item relative" style={{ animationDelay: `${idx * 0.07}s` }}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/admin'}
                    className={({ isActive }) => `flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-3 rounded-xl text-green-900 font-semibold golden-frame ${isActive ? 'active bg-blue-100' : 'hover:bg-green-50'}`}
                  >
                    <span className={sidebarCollapsed ? 'text-2xl' : 'flex-shrink-0'}>{item.icon}</span>
                    {!sidebarCollapsed && <span className="truncate text-sm">{item.name}</span>}
                    {sidebarCollapsed && (
                      <span className="sidebar-tooltip">{item.name}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          
          {/* Logout button at bottom of sidebar */}
          <div className="pt-4 border-t flex-shrink-0">
            <button
              onClick={handleLogout}
              className={`w-full px-3 py-3 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl hover:scale-105 shadow-lg transition-all duration-300 border-2 border-red-300 logout-button font-semibold text-sm ${
                sidebarCollapsed ? 'flex justify-center' : ''
              }`}
              title={sidebarCollapsed ? "Logout" : ""}
            >
              {sidebarCollapsed ? <FaSignOutAlt className="text-xl" /> : 'Logout'}
            </button>
          </div>
        </aside>

        {/* Hamburger menu for mobile */}
        <button
          className="md:hidden fixed top-4 left-4 z-50 bg-white rounded-full p-3 shadow-xl border-2 border-blue-400 hover:scale-105 hover:shadow-emerald/40 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open sidebar"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-700">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Sidebar drawer overlay for mobile */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 flex flex-col">
            {/* Overlay background */}
            <div
              className="bg-black/50 flex-1 animate-fadein"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            />
            {/* Sidebar drawer */}
            <aside className="w-72 bg-gray-900 text-white p-4 flex flex-col animate-slidein-left transition-all duration-700 rounded-3xl shadow-2xl border-2 border-blue-800 fixed top-0 left-0 h-full overflow-hidden">
              <nav className="flex-1 overflow-y-auto overflow-x-hidden pr-2">
                <ul className="space-y-3">
                  {navItems.map((item) => (
                    <li key={item.name}>
                      <NavLink
                        to={item.path}
                        end={item.path === '/admin'}
                        className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl font-semibold golden-frame truncate ${isActive ? 'active bg-blue-100 text-green-900' : 'text-white hover:bg-green-800'}`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        {item.icon}
                        <span className="truncate">{item.name}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="pt-4 border-t border-blue-800 flex-shrink-0">
                <button
                  onClick={() => { setSidebarOpen(false); handleLogout(); }}
                  className="w-full px-4 py-3 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl hover:scale-105 shadow-lg transition-all duration-300 border-2 border-red-300 logout-button font-semibold"
                >
                  Logout
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* Main Content (changes with nested routes) */}
        <main 
          className={`flex-1 p-0 transition-all duration-300 ml-4 flex flex-col h-auto min-h-[115vh] ${
            sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'
          }`}
        >
          <div className="bg-white/90 backdrop-blur-lg rounded-2xl md:rounded-3xl p-2 md:p-4 border-2 border-blue-200 w-full min-w-0 flex flex-col justify-start items-center overflow-auto mx-0" style={{marginTop: 0}}>
            <Outlet />
          </div>
        </main>
        </div>
      </div> 
    </>
  );
}